import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    IconButton,
    InputAdornment,
    Link,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {
    Eye,
    EyeOff,
    ShieldCheck,
    Users,
    TrendingUp,
    Award,
    DollarSign,
    CheckCircle,
    Lock,
} from 'lucide-react';
import { epRegister, sendEPEmailVerificationOtp, verifyEPEmailOtp } from '../../services/epAuthService';
import { useColorMode } from '../../context/ColorModeContext';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface FormData {
    name: string;
    email: string;
    password: string;
    confirmPassword: string;
    otp: string;
}

interface FormErrors {
    name?: string;
    email?: string;
    password?: string;
    confirmPassword?: string;
    otp?: string;
}

const PartnersPage: React.FC = () => {
    const navigate = useNavigate();
    const { mode } = useColorMode();
    const isDarkMode = mode === 'dark';

    const colors = {
        pageBg: isDarkMode ? '#0b1220' : '#f4f6fb',
        sectionBg: isDarkMode ? '#111827' : 'white',
        cardBg: isDarkMode ? '#131d31' : 'white',
        softBg: isDarkMode ? '#1a2740' : '#f4f6fb',
        textPrimary: isDarkMode ? '#e2e8f0' : '#0f172a',
        textSecondary: isDarkMode ? '#94a3b8' : '#64748b',
        cardBorder: isDarkMode ? '1px solid rgba(148, 163, 184, 0.22)' : '1px solid rgba(15, 23, 42, 0.08)',
    };

    const inputSx = isDarkMode ? {
        '& .MuiInputLabel-root': {
            color: '#94a3b8',
        },
        '& .MuiOutlinedInput-root': {
            color: '#e2e8f0',
            '& fieldset': {
                borderColor: 'rgba(148, 163, 184, 0.35)',
            },
            '&:hover fieldset': {
                borderColor: 'rgba(165, 180, 252, 0.6)',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#8b9cff',
            },
        },
    } : undefined;

    const [showOtpStep, setShowOtpStep] = useState(false);
    const [formData, setFormData] = useState<FormData>({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        otp: '',
    });

    const [errors, setErrors] = useState<FormErrors>({});
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData((prev) => ({ ...prev, [name]: value }));

        if (errors[name as keyof FormErrors]) {
            setErrors((prev) => ({ ...prev, [name]: undefined }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!EMAIL_REGEX.test(formData.email)) {
            newErrors.email = 'Please enter a valid email';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInitialSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!validateForm()) return;

        setLoading(true);
        try {
            // Send OTP to email
            await sendEPEmailVerificationOtp(formData.email);
            toast.success('OTP sent to your email! Please verify to complete registration.');
            setShowOtpStep(true);
        } catch (error: any) {
            const message = error?.response?.data?.msg || error?.message || 'Failed to send OTP';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyAndRegister = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!formData.otp.trim()) {
            setErrors({ otp: 'OTP is required' });
            return;
        }

        if (formData.otp.length !== 6) {
            setErrors({ otp: 'OTP must be 6 digits' });
            return;
        }

        setLoading(true);
        try {
            // Verify OTP
            await verifyEPEmailOtp(formData.email, formData.otp);

            // Register partner
            await epRegister({
                name: formData.name,
                email: formData.email,
                password: formData.password,
            });

            toast.success('Registration successful! Please login to continue.');
            navigate('/login');
        } catch (error: any) {
            const message = error?.response?.data?.msg || error?.response?.data?.message || error?.message || 'Verification failed';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    const benefits = [
        {
            icon: <DollarSign size={32} />,
            title: 'Earn Commission',
            description: 'Get rewarded for every student you bring to our platform',
        },
        {
            icon: <Users size={32} />,
            title: 'Build Your Network',
            description: 'Connect with students and help them achieve their academic goals',
        },
        {
            icon: <TrendingUp size={32} />,
            title: 'Track Performance',
            description: 'Access detailed analytics and insights on your dashboard',
        },
        {
            icon: <Award size={32} />,
            title: 'Recognition & Support',
            description: 'Get dedicated support and recognition for top performers',
        },
    ];

    const features = [
        'Dedicated partner dashboard',
        'Real-time enrollment tracking',
        'Marketing materials & resources',
        'Flexible commission structure',
        'Monthly payouts',
        'Priority support',
    ];

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: colors.pageBg, color: colors.textPrimary }}>
            {/* Hero Section */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #5a4bfc 0%, #00b894 100%)',
                    color: 'white',
                    py: 8,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Container maxWidth="lg">
                    <Stack spacing={3} alignItems="center" textAlign="center">
                        <Box
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: 3,
                                background: 'rgba(255, 255, 255, 0.2)',
                                backdropFilter: 'blur(10px)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <ShieldCheck size={48} />
                        </Box>
                        <Typography variant="h2" fontWeight={800} sx={{ maxWidth: 700 }}>
                            Become an Enrollment Partner
                        </Typography>
                        <Typography variant="h6" sx={{ maxWidth: 600, opacity: 0.95 }}>
                            Join our partner program and help students succeed while earning rewards.
                            Grow your network and make a real impact in education.
                        </Typography>
                    </Stack>
                </Container>
            </Box>

            {/* Benefits Section */}
            <Container maxWidth="lg" sx={{ py: 8 }}>
                <Typography variant="h4" fontWeight={700} textAlign="center" gutterBottom sx={{ color: colors.textPrimary }}>
                    Why Partner With Us?
                </Typography>
                <Typography
                    variant="body1"
                    textAlign="center"
                    sx={{ mb: 6, maxWidth: 700, mx: 'auto', color: colors.textSecondary }}
                >
                    Join hundreds of partners who are already making a difference and earning rewards
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 4 }}>
                    {benefits.map((benefit, index) => (
                        <Box key={index}>
                            <Card
                                sx={{
                                    height: '100%',
                                    textAlign: 'center',
                                    p: 3,
                                    backgroundColor: colors.cardBg,
                                    color: colors.textPrimary,
                                    border: colors.cardBorder,
                                    boxShadow: isDarkMode ? '0 8px 24px rgba(0,0,0,0.35)' : '0 4px 16px rgba(0,0,0,0.08)',
                                    transition: 'transform 0.3s ease',
                                    '&:hover': {
                                        transform: 'translateY(-8px)',
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        borderRadius: 2,
                                        background: 'linear-gradient(135deg, #5a4bfc 0%, #00b894 100%)',
                                        color: 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mx: 'auto',
                                        mb: 2,
                                    }}
                                >
                                    {benefit.icon}
                                </Box>
                                <Typography variant="h6" fontWeight={700} gutterBottom>
                                    {benefit.title}
                                </Typography>
                                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                                    {benefit.description}
                                </Typography>
                            </Card>
                        </Box>
                    ))}
                </Box>
            </Container>

            {/* Features & Signup Section */}
            <Box sx={{ bgcolor: colors.sectionBg, py: 8 }}>
                <Container maxWidth="lg">
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 6, alignItems: 'center' }}>
                        {/* Features List */}
                        <Box>
                            <Typography variant="h4" fontWeight={700} gutterBottom sx={{ color: colors.textPrimary }}>
                                What You Get
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 4, color: colors.textSecondary }}>
                                As an enrollment partner, you'll have access to exclusive tools and benefits
                                designed to help you succeed.
                            </Typography>

                            <Stack spacing={2}>
                                {features.map((feature, index) => (
                                    <Stack direction="row" spacing={2} alignItems="center" key={index}>
                                        <CheckCircle size={24} color="#00b894" />
                                        <Typography variant="body1" sx={{ color: colors.textPrimary }}>{feature}</Typography>
                                    </Stack>
                                ))}
                            </Stack>

                            <Box sx={{ mt: 4, p: 3, bgcolor: colors.softBg, borderRadius: 2, border: colors.cardBorder }}>
                                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                                    <strong>Already a partner?</strong>{' '}
                                    <Link
                                        component={RouterLink}
                                        to="/login"
                                        underline="hover"
                                        fontWeight={600}
                                        sx={{ color: '#7c8cff' }}
                                    >
                                        Login here
                                    </Link>{' '}
                                    to access your dashboard
                                </Typography>
                            </Box>
                        </Box>

                        {/* Signup Form */}
                        <Box>
                            <Card
                                sx={{
                                    backgroundColor: colors.cardBg,
                                    border: colors.cardBorder,
                                    boxShadow: isDarkMode ? '0 12px 36px rgba(0,0,0,0.4)' : '0 8px 32px rgba(0,0,0,0.12)',
                                    borderRadius: 3,
                                }}
                            >
                                <CardContent sx={{ p: 4 }}>
                                    <Typography variant="h5" fontWeight={700} gutterBottom textAlign="center" sx={{ color: colors.textPrimary }}>
                                        {showOtpStep ? 'Verify Your Email' : 'Join Now'}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        textAlign="center"
                                        sx={{ mb: 3, color: colors.textSecondary }}
                                    >
                                        {showOtpStep
                                            ? 'Enter the 6-digit code sent to your email'
                                            : 'Fill in your details to get started'
                                        }
                                    </Typography>

                                    {!showOtpStep ? (
                                        // Initial Form
                                        <form onSubmit={handleInitialSubmit}>
                                            <Stack spacing={2.5}>
                                                <TextField
                                                    label="Full Name"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    required
                                                    fullWidth
                                                    error={Boolean(errors.name)}
                                                    helperText={errors.name}
                                                    autoComplete="name"
                                                    sx={inputSx}
                                                />

                                                <TextField
                                                    label="Email"
                                                    type="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    required
                                                    fullWidth
                                                    error={Boolean(errors.email)}
                                                    helperText={errors.email}
                                                    autoComplete="email"
                                                    sx={inputSx}
                                                />

                                                <TextField
                                                    label="Password"
                                                    type={showPassword ? 'text' : 'password'}
                                                    name="password"
                                                    value={formData.password}
                                                    onChange={handleChange}
                                                    required
                                                    fullWidth
                                                    error={Boolean(errors.password)}
                                                    helperText={errors.password}
                                                    autoComplete="new-password"
                                                    sx={inputSx}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    aria-label="toggle password visibility"
                                                                    onClick={() => setShowPassword(!showPassword)}
                                                                    edge="end"
                                                                    sx={{ color: isDarkMode ? '#cbd5e1' : undefined }}
                                                                >
                                                                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />

                                                <TextField
                                                    label="Confirm Password"
                                                    type={showConfirmPassword ? 'text' : 'password'}
                                                    name="confirmPassword"
                                                    value={formData.confirmPassword}
                                                    onChange={handleChange}
                                                    required
                                                    fullWidth
                                                    error={Boolean(errors.confirmPassword)}
                                                    helperText={errors.confirmPassword}
                                                    autoComplete="new-password"
                                                    sx={inputSx}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    aria-label="toggle password visibility"
                                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                                    edge="end"
                                                                    sx={{ color: isDarkMode ? '#cbd5e1' : undefined }}
                                                                >
                                                                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />

                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    size="large"
                                                    disabled={loading}
                                                    sx={{
                                                        mt: 1,
                                                        background: 'linear-gradient(135deg, #5a4bfc 0%, #00b894 100%)',
                                                        '&:hover': {
                                                            background: 'linear-gradient(135deg, #4a3bec 0%, #009874 100%)',
                                                        },
                                                    }}
                                                >
                                                    {loading ? 'Sending OTP…' : 'Continue'}
                                                </Button>
                                            </Stack>
                                        </form>
                                    ) : (
                                        // OTP Verification Form
                                        <form onSubmit={handleVerifyAndRegister}>
                                            <Stack spacing={2.5}>
                                                <TextField
                                                    label="Enter OTP"
                                                    name="otp"
                                                    value={formData.otp}
                                                    onChange={handleChange}
                                                    required
                                                    fullWidth
                                                    error={Boolean(errors.otp)}
                                                    helperText={errors.otp}
                                                    inputProps={{ maxLength: 6 }}
                                                    sx={inputSx}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Lock size={20} color={isDarkMode ? '#cbd5e1' : undefined} />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />

                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    size="large"
                                                    disabled={loading}
                                                    sx={{
                                                        background: 'linear-gradient(135deg, #5a4bfc 0%, #00b894 100%)',
                                                        '&:hover': {
                                                            background: 'linear-gradient(135deg, #4a3bec 0%, #009874 100%)',
                                                        },
                                                    }}
                                                >
                                                    {loading ? 'Verifying…' : 'Verify & Complete Registration'}
                                                </Button>

                                                <Button
                                                    onClick={() => setShowOtpStep(false)}
                                                    variant="text"
                                                    size="small"
                                                    sx={{ color: isDarkMode ? '#cbd5e1' : undefined }}
                                                >
                                                    Back to Form
                                                </Button>
                                            </Stack>
                                        </form>
                                    )}
                                </CardContent>
                            </Card>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* Footer CTA */}
            <Box sx={{ bgcolor: colors.pageBg, py: 6, textAlign: 'center', borderTop: colors.cardBorder }}>
                <Container maxWidth="md">
                    <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                        Have questions?{' '}
                        <Link component={RouterLink} to="/" underline="hover" fontWeight={600} sx={{ color: '#7c8cff' }}>
                            Contact our support team
                        </Link>
                    </Typography>
                </Container>
            </Box>
        </Box>
    );
};

export default PartnersPage;
